const PGAdapter = require('./../../common/pg-adapter')
const { getOrderByClause, arrayOrStringPreparer } = require('../../tools/sql-tools')

const {
  SHARED_ROUTE_COLUMNS,
  SHARED_VIRTUAL_COLUMNS,
  SHARED_GET_RELATED,
  SHARED_COLUMN_PREPARERS
} = require('./route-pg-adapter-shared')

const ROUTE_VIEW_TABLE_NAME = 'avocado.vw_route_with_program'

// SQL predicates for "does this route still have any shipments?".
//
// A route's program/state/origin/funder are all derived from its shipments, so
// it loses that linkage whenever it loses its shipments — both when it is
// cancelled and when all of its stops are moved to another route (move-stops).
// Such "orphaned" routes can't be scoped by the usual shipment-derived filters,
// so those filters are relaxed to let shipment-less routes through and a
// separate visibility rule (see _applyOrphanedRouteVisibility) decides who may
// see them.
const routeHasShipmentsSql = `
  EXISTS (
    SELECT 1 FROM avocado.data_shipment shipments
    WHERE shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id
  )`
const routeHasNoShipmentsSql = `NOT ${routeHasShipmentsSql}`

// An orphaned route is only shown to the user who last touched it
// (`updated_by`) and only for a limited window afterwards (`updated_at`).
// For cancelled routes this is issue #20244 (option 3 + product decision); the
// same rule covers routes abandoned via move-stops. RLS scopes routes to the org.
const ORPHANED_ROUTE_MAX_AGE_MONTHS = 2

class RouteViewPGDataAdapter extends PGAdapter {
  constructor (pgConnection, user, logger, options) {
    const { withGlobalAccessUser = false } = options || {}

    // The shipment-derived filters (state, program_ids, origin_id) are all
    // relaxed the same way: `routeHasNoShipmentsSql OR <the actual match>`. This
    // lets orphaned (shipment-less) routes pass these filters; whether they are
    // actually shown is then decided by _applyOrphanedRouteVisibility.
    const columnPreparers = {
      ...SHARED_COLUMN_PREPARERS,
      status: arrayOrStringPreparer,
      program_ids: (key, value) => {
        const arrayValue = Array.isArray(value) ? value : [value]
        return [{
          value: arrayValue,
          statement: `(${routeHasNoShipmentsSql} OR "${key}" && %INDEX%::TEXT[])`
        }]
      },
      state: (key, value) => {
        const states = Array.isArray(value) ? value : [value]
        if (states.length === 1) {
          // Single state - use simple ILIKE
          return [{
            value: states[0],
            statement: `
                (
                  ${routeHasNoShipmentsSql}
                OR
                  EXISTS (
                    SELECT 1 FROM avocado.data_shipment shipments
                    WHERE
                      shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                      shipments.destination_id ILIKE '%:state:'||%INDEX%||':%'
                  )
                )
              `
          }]
        }
        // Multiple states - use ILIKE ANY with array of patterns
        return [{
          value: states.map(s => '%:state:' + s + ':%'),
          statement: `
              (
                ${routeHasNoShipmentsSql}
              OR
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                    shipments.destination_id ILIKE ANY(%INDEX%::TEXT[])
                )
              )
          `
        }]
      },
      origin_id: (key, value) => [
        {
          value,
          statement: `
              (
                ${routeHasNoShipmentsSql}
              OR
                EXISTS (
                  SELECT 1 FROM avocado.data_shipment shipments
                  WHERE
                    shipments.route_id = ${ROUTE_VIEW_TABLE_NAME}.couchdb_id AND
                    shipments.origin_id ILIKE %INDEX%||'%'
                )
              )
            `
        }
      ]
    }

    super(
      pgConnection,
      ROUTE_VIEW_TABLE_NAME,
      user,
      [
        ...SHARED_ROUTE_COLUMNS,
        'program_ids',
        'origin_id',
        'state' /** not a real column, it's only to support filtering */
      ],
      undefined,
      columnPreparers,
      logger,
      {
        getRelated: SHARED_GET_RELATED,
        virtualColumns: SHARED_VIRTUAL_COLUMNS,
        securedByOrganisationId: true,
        withGlobalAccessUser
      }
    )
  }

  /**
   * Returns the ISO cutoff date: orphaned routes last touched before this are hidden.
   * (issue #20244 / move-stops — only show recently orphaned routes.)
   */
  _getOrphanedRouteCutoffDate () {
    const cutoff = new Date()
    cutoff.setMonth(cutoff.getMonth() - ORPHANED_ROUTE_MAX_AGE_MONTHS)
    return cutoff.toJSON()
  }

  /**
   * Appends the orphaned-route visibility rule to a WHERE clause.
   *
   * A route with shipments is scoped by the (relaxed) shipment-derived filters as
   * usual; a route without shipments — cancelled, or abandoned via move-stops —
   * has no program/state/origin to match on, so it is only shown to whoever last
   * touched it (updated_by) and only within the recency window:
   *
   *   <route has shipments> OR (updated_by = <me> AND updated_at >= <cutoff>)
   *
   * The rule only constrains shipment-less rows, so it can be applied to any route
   * query (list or counts). Global-access users (admin/reporting, no org context)
   * and contexts without a known username are exempt: they keep seeing everything.
   *
   * @param {string} whereText - WHERE clause from getWhereCondition ('' or 'WHERE ...')
   * @param {Array} whereValues - its positional values
   * @returns {{ whereText: string, whereValues: Array }} augmented clause + values
   */
  _applyOrphanedRouteVisibility (whereText, whereValues) {
    if (!this.username || this.withGlobalAccessUser) {
      return { whereText, whereValues }
    }

    // New placeholders start after the existing values.
    const offset = whereValues.length
    const clause =
      `(${routeHasShipmentsSql} OR ("updated_by" = $${offset + 1} AND "updated_at" >= $${offset + 2}))`
    const values = [this.username, this._getOrphanedRouteCutoffDate()]

    return {
      whereText: whereText ? `${whereText} AND ${clause}` : `WHERE ${clause}`,
      whereValues: [...whereValues, ...values]
    }
  }

  /**
   * Returns a json_build_object SQL fragment for the given data_location alias.
   * Keeps the field list in one place instead of duplicating it for origin and destination.
   */
  _locationJsonBuildObject (alias) {
    return `json_build_object(
                '_id', ${alias}.fsid,
                'name', ${alias}.name_of_pharmacy,
                'state', ${alias}.state,
                'lga', ${alias}.lga,
                'zone', ${alias}.zone,
                'level', ${alias}.supply_chain_level
              )`
  }

  addRelatedCarrierColumn () {
    return `
      (
        SELECT row_to_json(org) FROM avocado.data_organisation org
        WHERE org.id = ${this.tableName}.carrier_id
        LIMIT 1
      ) AS carrier
    `
  }

  addRelatedShipmentsColumn () {
    return `
      COALESCE(
        (
          SELECT json_agg(DISTINCT s.*)
          FROM avocado.data_shipment s
          WHERE s.route_id = ${this.tableName}.couchdb_id
        ),
        '[]'
      ) AS shipments
    `
  }

  /**
   * Returns a LATERAL JOIN subquery that enriches shipments with location + program data.
   * Uses LATERAL instead of a correlated subquery so the planner can push down
   * the route filter and use indexes on data_shipment.route_id more effectively.
   */
  addEnrichedShipmentsLateral () {
    return `
      LEFT JOIN LATERAL (
        SELECT COALESCE(json_agg(enriched_shipment), '[]') AS shipments
        FROM (
          SELECT DISTINCT ON (s.id)
            s.*,
            ${this._locationJsonBuildObject('dl_dest')} AS destination,
            ${this._locationJsonBuildObject('dl_orig')} AS origin,
            json_build_object(
              'id', dp.fsid,
              'name', dp.name
            ) AS program,
            CASE WHEN df.id IS NOT NULL
              THEN json_build_object('_id', df.couchdb_id, 'name', df.name)
              ELSE NULL
            END AS funder
          FROM avocado.data_shipment s
          LEFT JOIN avocado.data_location dl_dest ON dl_dest.fsid = s.destination_id
          LEFT JOIN avocado.data_location dl_orig ON dl_orig.fsid = s.origin_id
          LEFT JOIN avocado.data_program dp ON dp.fsid = s.program_id
          LEFT JOIN avocado.data_funder df ON df.couchdb_id = s.funder_id
          WHERE s.route_id = ${this.tableName}.couchdb_id
        ) enriched_shipment
      ) enriched_shipments ON true
    `
  }

  async getList ({
    ordering = this.idColumn,
    filter = {},
    limit = 50,
    offset = 0,
    tableExpression = null,
    useTableAsExpression = false,
    search = null,
    skipCount = false,
    skipData = false,
    getRelated
  } = {}) {
    const base = this.getWhereCondition(filter, search)
    // issue #20244 / move-stops: hide orphaned routes that aren't the current user's recent ones
    const { whereText, whereValues } = this._applyOrphanedRouteVisibility(base.whereText, base.whereValues)
    const resultList = {}

    if (!skipData) {
      const sortText = getOrderByClause(
        ordering,
        this.columns.concat(this.virtualColumns),
        (tableExpression && useTableAsExpression ? this.tableName : undefined)
      )
      /*
      Postgresql usage reminder ():
      > ASC order is the default. Ascending order puts smaller values first,
      where “smaller” is defined in terms of the < operator.
      - https://www.postgresql.org/docs/13/queries-order.html
    */
      const values = whereValues.concat([limit, offset])

      const fromTable = tableExpression || this.tableName

      const text = `
        SELECT
          ${fromTable}.*,
          ${this.addRelatedCarrierColumn()},
          enriched_shipments.shipments
        FROM ${fromTable}
        ${this.addEnrichedShipmentsLateral()}
        ${whereText}
        ${sortText}
        LIMIT $${values.length - 1} OFFSET $${values.length};`

      let { rows: results } = await this.pgConnection.query({ text, values })

      resultList.results = results
    }

    if (!skipCount) { // Sometimes we don't need it so we may skip it for performance reasons
      resultList.count = await this.getCount(undefined, whereText, whereValues, tableExpression, useTableAsExpression)
    }

    return resultList
  }

  /**
   * Get counts for all statuses in a single query using conditional aggregation.
   * Replaces the previous N+1 query pattern (one COUNT per status + one for total).
   *
   * @param {Object} filter - The base filter (without status) from _createRouteFilter
   * @param {string[]} statusesToCheck - The statuses the user is allowed to see
   * @returns {Object} e.g. { total: 5, provisional: 2, approved: 1, ... }
   */
  async getCountsByStatus (filter, statusesToCheck) {
    // Separate status from filter — we count all requested statuses in one query
    const { status: _ignoredStatus, ...filterWithoutStatus } = filter

    const base = this.getWhereCondition(filterWithoutStatus)
    // issue #20244 / move-stops: the counts must match the lists — orphaned routes
    // only count for the user who last touched them. Applied to the shared WHERE,
    // it leaves routes that still have shipments untouched before CASE bucketing.
    const { whereText, whereValues } = this._applyOrphanedRouteVisibility(base.whereText, base.whereValues)

    // Build COUNT(CASE WHEN ...) columns dynamically from the statuses.
    // Param indexes start after whereValues (which now includes the visibility values).
    const caseColumns = statusesToCheck.map((status, i) => {
      const paramIndex = whereValues.length + i + 1
      return `COUNT(CASE WHEN "status" = $${paramIndex} THEN 1 END) AS "${status}"`
    })

    const mainQueryText = `
      SELECT
        ${caseColumns.join(',\n        ')}
      FROM ${this.tableName}
      ${whereText}
    `
    const mainQueryValues = [...whereValues, ...statusesToCheck]

    const { rows: [mainRow] } = await this.pgConnection.query({
      text: mainQueryText,
      values: mainQueryValues
    })

    // Convert string counts to numbers and derive total as their sum
    // so it only reflects statuses the user is allowed to see
    const result = {}
    let total = 0
    for (const status of statusesToCheck) {
      const count = Number(mainRow[status])
      result[status] = count
      total += count
    }
    result.total = total

    return result
  }
}

module.exports = RouteViewPGDataAdapter
